/**
 * Report export engine — turns an assembled report model into a downloadable
 * file (PDF, CSV, or Word). Used by the Reports builder (REFReports.jsx).
 *
 * Report model shape:
 * {
 *   title, scopeLabel, periodLabel, personaLabel, generatedAt,
 *   sections: {
 *     performance: { include, scores:{reliability,..}, trend },
 *     narrative:   { include, text },
 *     trls:        { include, items:[...] },
 *     caseStudies: { include, items:[{title,industry,tags}] },
 *   }
 * }
 */
import { jsPDF } from 'jspdf';
import { REPS_COLORS, oeBand } from './constants';
import { CONDITION_LOG_COLUMNS, conditionLogRow } from './reportRows';
import { conditionStatusColor } from './trlConstants';

const NAVY = '#1F3864'; // report title color — the "/10" uses it too (Tim D09 image 4/5, P5-02)

const QUADS = ['reliability', 'efficiency', 'productivity', 'safety'];
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

function hexToRgb(hex) {
  const h = String(hex || '#666').replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function fileBase(report) {
  return (report.title || 'REPSit-Report').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'REPSit-Report';
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------
export function exportReportPDF(report) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const M = 54;
  const W = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  let y = M;

  const ensure = (h) => { if (y + h > PH - M) { doc.addPage(); y = M; } };
  const line = (t, opts = {}) => {
    const size = opts.size || 10;
    doc.setFontSize(size);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || [40, 40, 40]));
    const x = opts.x || M;
    const lines = doc.splitTextToSize(String(t), opts.width || (W - M - x));
    lines.forEach((ln) => { ensure(size + 4); doc.text(ln, x, y); y += size + 4; });
  };
  const gap = (h = 8) => { y += h; };
  const rule = () => { ensure(12); doc.setDrawColor(210); doc.line(M, y, W - M, y); y += 12; };

  line(report.title, { size: 18, bold: true, color: [31, 56, 100] });
  line(`${report.scopeLabel}   •   ${report.periodLabel}`, { size: 10, color: [110, 110, 110] });
  line(`Generated ${report.generatedAt}   •   ${report.personaLabel}`, { size: 9, color: [150, 150, 150] });
  gap(4); rule();

  const s = report.sections || {};

  if (s.performance?.include && s.performance.scores) {
    line('REPS Performance', { size: 13, bold: true, color: [46, 117, 182] }); gap(2);
    // P5-02 (D09 image 4/5): quadrant name in its REPS color, score in the threshold band color,
    // "/10" in the report-title navy, status as a color-coded badge.
    QUADS.forEach((q) => {
      const v = s.performance.scores[q];
      if (v == null) return;
      const band = oeBand(v, s.performance.thresholds?.[q] ?? 6);
      ensure(18);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...hexToRgb(REPS_COLORS[q])); doc.text(cap(q), M, y);
      doc.setTextColor(...hexToRgb(band.color)); doc.text(v.toFixed(1), M + 110, y);
      doc.setTextColor(...hexToRgb(NAVY)); doc.text('/ 10', M + 140, y);
      const bw = doc.getTextWidth(band.label) + 12;
      doc.setFillColor(...hexToRgb(band.color)); doc.roundedRect(M + 180, y - 9, bw, 13, 3, 3, 'F');
      doc.setFontSize(8); doc.setTextColor(255, 255, 255); doc.text(band.label, M + 186, y);
      y += 18;
    });
    if (s.performance.trend) line(`Trend: ${s.performance.trend}`, { size: 9, color: [120, 120, 120] });
    gap(4); rule();
  }

  if (s.narrative?.include && s.narrative.text) {
    line('Executive Briefing', { size: 13, bold: true, color: [46, 117, 182] }); gap(2);
    s.narrative.text.split('\n\n').forEach((p) => { line(p, { size: 10 }); gap(4); });
    rule();
  }

  if (s.trls?.include) {
    line(`OE Condition & REF Action Log  (${s.trls.items.length})`, { size: 13, bold: true, color: [46, 117, 182] }); gap(2);
    if (s.trls.items.length === 0) {
      line('No OE Conditions logged in this period for this scope.', { size: 10, color: [120, 120, 120] });
    }
    // P2-23 / P5-06 columns: ID, Title, Type/Subtype, REPS, Severity (ITM/L1/L2), Status, Owner, Priority/Due, REF Actions
    s.trls.items.forEach((t) => {
      const r = conditionLogRow(t, report.org);
      line(`${r.id ? `${r.id}  ·  ` : ''}${r.title}`, { size: 10, bold: true });
      line(`${r.typeSubtype}   •   REPS: ${r.reps || '—'}   •   Severity Entrant/L1/L2: ${r.severity}`, { size: 9, color: [110, 110, 110], x: M + 14 });
      line(`Status: ${r.status}${r.owner ? `   •   Owner: ${r.owner}` : ''}${r.priorityDue ? `   •   Priority/Due: ${r.priorityDue}` : ''}`, { size: 9, color: [110, 110, 110], x: M + 14 });
      if (r.refActions) line(`REF: ${r.refActions}`, { size: 9, color: [110, 110, 110], x: M + 14 });
      gap(3);
    });
    gap(2); rule();
  }

  if (s.caseStudies?.include) {
    line(`Case Study Benchmarks  (${s.caseStudies.items.length})`, { size: 13, bold: true, color: [46, 117, 182] }); gap(2);
    const sc = s.caseStudies.scopeScores;
    if (sc) line(`Your scope:  ${QUADS.map((q) => `${q[0].toUpperCase()} ${sc[q] != null ? Number(sc[q]).toFixed(1) : '—'}`).join('   ')}   (higher is worse)`, { size: 9, color: [110, 110, 110] });
    gap(2);
    s.caseStudies.items.forEach((c) => {
      line(`• ${c.title}`, { size: 10, bold: true });
      line(`${[c.categoryLabel, c.industry].filter(Boolean).join('   •   ')}`, { size: 9, color: [110, 110, 110], x: M + 14 });
      if (c.repsScores) line(`Document:  ${QUADS.map((q) => `${q[0].toUpperCase()} ${c.repsScores[q] != null ? Number(c.repsScores[q]).toFixed(1) : '—'}`).join('   ')}     You vs document:  ${QUADS.map((q) => `${q[0].toUpperCase()} ${c.delta?.[q] == null ? '—' : (c.delta[q] > 0 ? '+' : '') + Number(c.delta[q]).toFixed(1)}`).join('   ')}`, { size: 9, color: [60, 60, 60], x: M + 14 });
      gap(2);
    });
  }

  doc.save(`${fileBase(report)}.pdf`);
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------
export function exportReportCSV(report) {
  const esc = (v) => { const t = String(v ?? ''); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const rows = [];
  rows.push(['Report', report.title]);
  rows.push(['Scope', report.scopeLabel]);
  rows.push(['Period', report.periodLabel]);
  rows.push(['Generated', report.generatedAt]);
  rows.push([]);

  const s = report.sections || {};
  if (s.performance?.include && s.performance.scores) {
    rows.push(['REPS Performance']);
    rows.push(['Quadrant', 'Score', 'Band']);
    QUADS.forEach((q) => { const v = s.performance.scores[q]; if (v != null) rows.push([cap(q), v.toFixed(1), oeBand(v, s.performance.thresholds?.[q] ?? 6).label]); });
    rows.push([]);
  }
  if (s.trls?.include) {
    rows.push(['OE Condition & REF Action Log']);
    rows.push(CONDITION_LOG_COLUMNS.map(([, label]) => label));
    s.trls.items.forEach((t) => { const r = conditionLogRow(t, report.org); rows.push(CONDITION_LOG_COLUMNS.map(([key]) => r[key])); });
    rows.push([]);
  }
  if (s.caseStudies?.include) {
    rows.push(['Case Study Benchmarks']);
    rows.push(['Title', 'Category', 'Industry', 'Source', 'Reliability', 'Efficiency', 'Productivity', 'Safety', 'You vs doc: Reliability', 'You vs doc: Efficiency', 'You vs doc: Productivity', 'You vs doc: Safety', 'Tags']);
    if (s.caseStudies.scopeScores) rows.push(['Your scope', '', '', '', ...QUADS.map((q) => (s.caseStudies.scopeScores[q] != null ? Number(s.caseStudies.scopeScores[q]).toFixed(1) : '')), '', '', '', '', '']);
    s.caseStudies.items.forEach((c) => rows.push([c.title, c.categoryLabel || '', c.industry || '', c.source === 'org' ? 'My organization' : 'Industry library', ...QUADS.map((q) => (c.repsScores?.[q] != null ? Number(c.repsScores[q]).toFixed(1) : '')), ...QUADS.map((q) => (c.delta?.[q] != null ? Number(c.delta[q]).toFixed(1) : '')), (c.tags || []).join('|')]));
    rows.push([]);
  }
  if (s.narrative?.include && s.narrative.text) {
    rows.push(['Executive Briefing']);
    rows.push([s.narrative.text.replace(/\n+/g, '  ')]);
  }

  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${fileBase(report)}.csv`);
}

// ---------------------------------------------------------------------------
// Word — generated as a Word-compatible HTML document (.doc), opens & edits in
// Microsoft Word with no extra dependency. (Can be upgraded to native .docx.)
// ---------------------------------------------------------------------------
export function exportReportDoc(report) {
  const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const s = report.sections || {};
  let b = `<h1 style="color:#1F3864;font-family:Arial">${esc(report.title)}</h1>`;
  b += `<p style="color:#666;font-family:Arial">${esc(report.scopeLabel)} &bull; ${esc(report.periodLabel)}<br/>Generated ${esc(report.generatedAt)} &bull; ${esc(report.personaLabel)}</p><hr/>`;

  if (s.performance?.include && s.performance.scores) {
    b += `<h2 style="color:#2E75B6;font-family:Arial">REPS Performance</h2><ul style="font-family:Arial">`;
    QUADS.forEach((q) => {
      const v = s.performance.scores[q];
      if (v == null) return;
      const band = oeBand(v, s.performance.thresholds?.[q] ?? 6);
      b += `<li><b style="color:${REPS_COLORS[q]}">${cap(q)}</b>: <b style="color:${band.color}">${v.toFixed(1)}</b> <span style="color:${NAVY}">/ 10</span> &nbsp; <span style="background:${band.color};color:#fff;padding:1px 8px;border-radius:8px;font-size:9pt">${band.label}</span></li>`;
    });
    b += `</ul>`;
  }
  if (s.narrative?.include && s.narrative.text) {
    b += `<h2 style="color:#2E75B6;font-family:Arial">Executive Briefing</h2>` + s.narrative.text.split('\n\n').map((p) => `<p style="font-family:Arial">${esc(p)}</p>`).join('');
  }
  if (s.trls?.include) {
    b += `<h2 style="color:#2E75B6;font-family:Arial">OE Condition &amp; REF Action Log (${s.trls.items.length})</h2>`;
    if (s.trls.items.length === 0) {
      b += `<p style="font-family:Arial;color:#666">No OE Conditions logged in this period for this scope.</p>`;
    } else {
      b += `<table border="1" cellspacing="0" cellpadding="5" style="font-family:Arial;border-collapse:collapse;font-size:10pt"><tr style="background:#1F3864;color:#fff">${CONDITION_LOG_COLUMNS.map(([, label]) => `<th>${esc(label)}</th>`).join('')}</tr>`;
      s.trls.items.forEach((t) => {
        const r = conditionLogRow(t, report.org);
        b += `<tr>${CONDITION_LOG_COLUMNS.map(([key]) => (key === 'status' ? `<td><span style="color:${conditionStatusColor(t.status)};font-weight:bold">&#9679;</span> ${esc(r[key])}</td>` : `<td>${esc(r[key])}</td>`)).join('')}</tr>`;
      });
      b += `</table>`;
    }
  }
  if (s.caseStudies?.include) {
    b += `<h2 style="color:#2E75B6;font-family:Arial">Case Study Benchmarks (${s.caseStudies.items.length})</h2>`;
    b += `<p style="font-family:Arial;font-size:9pt;color:#666">Your scope compared against each document. Higher is worse; a negative "you vs document" number means your scope is doing better.</p>`;
    b += `<table border="1" cellspacing="0" cellpadding="5" style="font-family:Arial;border-collapse:collapse;font-size:10pt"><tr style="background:#1F3864;color:#fff"><th>Document</th><th>Reliability</th><th>Efficiency</th><th>Productivity</th><th>Safety</th></tr>`;
    if (s.caseStudies.scopeScores) b += `<tr style="background:#F2F2F2"><td><b>Your scope</b></td>${QUADS.map((q) => `<td align="center"><b>${s.caseStudies.scopeScores[q] != null ? Number(s.caseStudies.scopeScores[q]).toFixed(1) : '—'}</b></td>`).join('')}</tr>`;
    s.caseStudies.items.forEach((c) => {
      b += `<tr><td><b>${esc(c.title)}</b><br><span style="color:#666;font-size:9pt">${esc([c.categoryLabel, c.industry].filter(Boolean).join(' • '))}</span></td>${QUADS.map((q) => { const v = c.repsScores?.[q]; const d = c.delta?.[q]; return `<td align="center">${v != null ? Number(v).toFixed(1) : '—'}${d != null ? `<br><span style="font-size:9pt;color:${d < 0 ? '#2e7d32' : d > 0 ? '#b26a00' : '#666'}">${d > 0 ? '+' : ''}${Number(d).toFixed(1)}</span>` : ''}</td>`; }).join('')}</tr>`;
    });
    b += `</table>`;
  }

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>${esc(report.title)}</title></head><body>${b}</body></html>`;
  download(new Blob(['﻿' + html], { type: 'application/msword' }), `${fileBase(report)}.doc`);
}
